import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    padding: theme.spacing(2),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f8f9fa",
  borderRadius: 8,
  boxShadow: "none",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
    borderRadius: "8px 8px 0 0",
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& .MuiTypography-h5": {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  "& .MuiTypography-body1": {
    color: theme.palette.text.secondary,
  },
}));

const PreviewMappingModal = ({
  open,
  onClose,
  onBack,
  mappings,
  data, // First few rows of data for preview
}) => {
  // Get first row of data for preview
  const previewRow = data?.[0] || {};

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <HeaderSection>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={onBack}
            edge="start"
            sx={{ mr: 2, transform: "translateX(-8px)" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Typography variant="h5">Review Your Mappings</Typography>
            <Typography variant="body1">
              Verify how your data will be processed before generating
              estimates.
            </Typography>
          </div>
        </Box>
      </HeaderSection>

      <DialogContent sx={{ px: 0 }}>
        <Grid container spacing={3}>
          {/* Column Mappings Section */}
          <Grid xs={12}>
            <StyledPaper>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Column Mappings
              </Typography>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Required Field
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Mapped Column
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Sample Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(mappings).map(([field, value]) => (
                      <TableRow key={field}>
                        <TableCell component="th" scope="row">
                          <Typography variant="body2" fontWeight={500}>
                            {field}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {value.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={value.mapping}
                            size="small"
                            sx={{
                              borderRadius: "8px",
                              backgroundColor: "rgba(33, 150, 243, 0.08)",
                              color: "primary.main",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                mr: 1,
                              }}
                            >
                              {previewRow[value.mapping] || "—"}
                            </Typography>
                            {previewRow[value.mapping] ? (
                              <Tooltip
                                title="Data found in sample row"
                                placement="right"
                              >
                                <span>
                                  <CheckCircleIcon
                                    color="success"
                                    sx={{
                                      fontSize: 16,
                                      opacity: 0.8,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="No data found in sample row. Some columns support null values, but please ensure you have mapped all required fields correctly."
                                placement="right"
                              >
                                <span>
                                  <WarningIcon
                                    color="warning"
                                    sx={{
                                      fontSize: 16,
                                      opacity: 0.8,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>
          </Grid>

          {/* Data Preview Section */}
          <Grid xs={12}>
            <StyledPaper>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Ready to Generate Estimates
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your mappings have been configured successfully. Click "Confirm"
                to proceed with generating your tax estimates.
              </Typography>
            </StyledPaper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 3, px: 0, pb: 0 }}>
        <Button
          onClick={onBack}
          color="inherit"
          sx={{
            borderRadius: 2,
            px: 3,
          }}
        >
          Back
        </Button>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            boxShadow: "0 4px 12px rgba(33,150,243,0.2)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(33,150,243,0.3)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Confirm Mappings
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default PreviewMappingModal;
